/**
 * Onboarding Interaction Watcher Component
 *
 * Automatically starts tours when users interact with elements that have data-tour attributes
 * for the first time. This provides a better UX than beacons by triggering tours contextually.
 *
 * @author Andres Nava
 * @version 1.0.0
 */

import React, { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useOnboarding } from "../../contexts/OnboardingContext";
import { useAuth } from "../../contexts/AuthContext";
import { logger } from "../../utils/logger";
import { filterToursByRole } from "../../utils/tourHelpers";
import { isLabMissionTour } from "../../../shared/onboarding/tourCatalog";
import type { OnboardingFlow } from "../../types/onboarding";

/**
 * Storage key for tracking interacted elements
 */
const INTERACTED_ELEMENTS_KEY = "onboarding_interacted_elements";
const interactionTriggersEnabled =
  import.meta.env.VITE_ONBOARDING_INTERACTION_TRIGGERS === "true";

/**
 * Get interacted elements from localStorage
 */
const getInteractedElements = (): Set<string> => {
  try {
    const stored = localStorage.getItem(INTERACTED_ELEMENTS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return new Set(parsed);
    }
  } catch (error) {
    logger.warn("Error reading interacted elements from localStorage", {
      error,
    });
  }
  return new Set<string>();
};

/**
 * Save interacted element to localStorage
 */
const saveInteractedElement = (tourId: string): void => {
  try {
    const interacted = getInteractedElements();
    interacted.add(tourId);
    localStorage.setItem(
      INTERACTED_ELEMENTS_KEY,
      JSON.stringify(Array.from(interacted)),
    );
  } catch (error) {
    logger.warn("Error saving interacted element to localStorage", { error });
  }
};

/**
 * Nav bar data-tour IDs - clicking these is navigation, not a request for a tour.
 * Starting the tour on nav click blocks the user from actually navigating.
 */
const NAV_ELEMENT_IDS = new Set([
  "nav-logo",
  "nav-home",
  "nav-tac-shop",
  "nav-products-button",
  "nav-products-tacticore",
  "nav-products-apex-k9",
  "nav-products-apt-studio",
  "nav-products-chatapt",
  "nav-products-americon",
  "nav-products-myapt",
  "nav-products-teachapt",
  "nav-browse-button",
  "nav-about-us-button",
  "nav-user-menu",
  "nav-events",
  "nav-instructors",
  "nav-agencies",
  "nav-venues",
]);

/**
 * Find tour that targets a specific data-tour element
 */
const findTourForElement = (
  dataTourId: string,
  tours: OnboardingFlow[],
): OnboardingFlow | null => {
  for (const tour of tours) {
    // Check if any step in this tour targets the element
    const hasTargetingStep = tour.steps.some((step) => {
      if (!step.target) return false;

      // Extract data-tour value from selector
      // Handles formats like: [data-tour="element-id"] or [data-tour='element-id']
      const match = step.target.match(/data-tour=["']([^"']+)["']/);
      if (match) {
        return match[1] === dataTourId;
      }

      // Also check if the selector directly matches
      return (
        step.target === `[data-tour="${dataTourId}"]` ||
        step.target === `[data-tour='${dataTourId}']`
      );
    });

    if (hasTargetingStep) {
      return tour;
    }
  }

  return null;
};

/**
 * Onboarding Interaction Watcher Component
 *
 * Watches for user interactions with elements that have data-tour attributes
 * and automatically starts the associated tour on first interaction.
 */
export const OnboardingInteractionWatcher: React.FC = () => {
  const location = useLocation();
  const { state, config, startTour, isFlowCompleted, getAvailableTours } =
    useOnboarding();
  const { user } = useAuth();
  const interactedRef = useRef<Set<string>>(getInteractedElements());
  const debounceTimerRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  /**
   * Handle interaction with a data-tour element
   */
  const handleElementInteraction = useCallback(
    async (dataTourId: string) => {
      // Don't trigger if onboarding is disabled or tour is already running
      if (!config.enabled || state.isRunning) {
        return;
      }

      // Don't trigger tours on home page
      if (location.pathname === "/") {
        return;
      }

      // Don't trigger on nav bar clicks - user is navigating, not asking for a tour
      if (NAV_ELEMENT_IDS.has(dataTourId)) {
        return;
      }

      // Check if we've already interacted with this element
      if (interactedRef.current.has(dataTourId)) {
        return;
      }

      // Get available tours
      const availableTours = getAvailableTours();
      if (!availableTours || availableTours.length === 0) {
        return;
      }

      // Filter tours by user role
      const roleFilteredTours = filterToursByRole(availableTours, user);

      // Find the tour that targets this element
      const tour = findTourForElement(dataTourId, roleFilteredTours);

      if (!tour) {
        logger.debug("No tour found for element", { dataTourId });
        return;
      }

      if (isLabMissionTour(tour.id)) {
        return;
      }

      // Check if tour has already been completed or skipped
      if (isFlowCompleted(tour.id)) {
        // Mark as interacted even if completed, so we don't keep checking
        saveInteractedElement(dataTourId);
        interactedRef.current.add(dataTourId);
        return;
      }

      // Check if this specific tour is already running
      if (state.currentFlowId === tour.id && state.isRunning) {
        // Mark as interacted so we don't try to start it again
        saveInteractedElement(dataTourId);
        interactedRef.current.add(dataTourId);
        return;
      }

      // Mark as interacted before starting tour to prevent duplicate triggers
      saveInteractedElement(dataTourId);
      interactedRef.current.add(dataTourId);

      logger.info("Auto-starting tour on element interaction", {
        dataTourId,
        tourId: tour.id,
        userRole: user?.role,
      });

      // Start the tour
      try {
        await startTour(tour.id);
      } catch (error) {
        logger.error("Error starting tour from interaction", {
          error,
          tourId: tour.id,
          dataTourId,
        });
        // Remove from interacted set if failed to allow retry
        interactedRef.current.delete(dataTourId);
      }
    },
    [
      config.enabled,
      state.isRunning,
      getAvailableTours,
      user,
      isFlowCompleted,
      startTour,
    ],
  );

  /**
   * Debounced interaction handler to prevent rapid-fire triggers
   */
  const debouncedHandleInteraction = useCallback(
    (dataTourId: string) => {
      // Clear existing timer for this element
      const existingTimer = debounceTimerRef.current.get(dataTourId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new timer
      const timer = setTimeout(() => {
        handleElementInteraction(dataTourId);
        debounceTimerRef.current.delete(dataTourId);
      }, 300); // 300ms debounce

      debounceTimerRef.current.set(dataTourId, timer);
    },
    [handleElementInteraction],
  );

  /**
   * Set up event listeners for data-tour elements
   */
  useEffect(() => {
    if (!config.enabled || !interactionTriggersEnabled) {
      return;
    }

    /**
     * Handle click events on data-tour elements
     */
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Find the closest element with data-tour attribute
      const elementWithTour = target.closest("[data-tour]") as HTMLElement;

      if (elementWithTour) {
        const dataTourId = elementWithTour.getAttribute("data-tour");
        if (dataTourId) {
          debouncedHandleInteraction(dataTourId);
        }
      }
    };

    /**
     * Handle focus events on data-tour elements (for keyboard navigation)
     */
    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement;

      if (target.hasAttribute("data-tour")) {
        const dataTourId = target.getAttribute("data-tour");
        if (dataTourId) {
          // Only trigger on focus for interactive elements (buttons, links, inputs)
          const isInteractive =
            target.tagName === "BUTTON" ||
            target.tagName === "A" ||
            target.tagName === "INPUT" ||
            target.tagName === "SELECT" ||
            target.tagName === "TEXTAREA" ||
            target.getAttribute("role") === "button" ||
            target.getAttribute("role") === "link" ||
            target.getAttribute("tabindex") !== null;

          if (isInteractive) {
            debouncedHandleInteraction(dataTourId);
          }
        }
      }
    };

    // Add event listeners
    document.addEventListener("click", handleClick, true); // Use capture phase
    document.addEventListener("focusin", handleFocus, true); // Use capture phase

    // Cleanup
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("focusin", handleFocus, true);

      // Clear all debounce timers
      debounceTimerRef.current.forEach((timer) => clearTimeout(timer));
      debounceTimerRef.current.clear();
    };
  }, [config.enabled, debouncedHandleInteraction]);

  // This component doesn't render anything
  return null;
};

export default OnboardingInteractionWatcher;
